// src/hooks/useMonthlyPaymentsAutomation.ts
// 🤖 HOOK DE AUTOMATIZACIÓN - Genera pagos mensuales automáticamente
// Se ejecuta cuando cualquier admin abre la app

import { useEffect, useState } from 'react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { generateMonthlyPayments } from '../services/monthlyPayments.service';
import { isMySqlEnabled } from '../services/apiClient';

interface AutomationStatus {

  isRunning: boolean;
  lastRun: Date | null;
  lastResult: {
    success: boolean;
    paymentsGenerated: number;
    errors: string[];
  } | null;
}

/**
 * 🤖 Hook que automatiza la generación de pagos mensuales
 * 
 * Cómo funciona:
 * 1. Se ejecuta cuando se monta el componente (al abrir la app)
 * 2. Verifica si ya se ejecutó hoy usando un documento global en Firestore
 * 3. Si no se ejecutó, genera los pagos del mes actual
 * 4. Guarda el resultado en Firestore para evitar que otros navegadores/dispositivos lo ejecuten
 * 
 * @param gymId - ID del gimnasio
 * @param enabled - Si está habilitada la automatización (por defecto true)
 */
export const useMonthlyPaymentsAutomation = (

  gymId: string | undefined,
  enabled: boolean = true
) => {
  const [status, setStatus] = useState<AutomationStatus>({
    isRunning: false,
    lastRun: null,
    lastResult: null
  });

  useEffect(() => {
    // Solo ejecutar si:
    // - Está habilitado
    // - Hay un gymId válido
    // - No se está ejecutando actualmente
    // - No estamos usando MySQL
    if (!enabled || !gymId || status.isRunning || isMySqlEnabled()) {
      return;
    }


    const runAutomation = async () => {
      try {
        const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
        const automationRef = doc(db, `gyms/${gymId}/settings/automation`);
        
        // 1. Verificar en Firestore si ya se ejecutó hoy globalmente (cuesta 1 sola lectura)
        const automationSnap = await getDoc(automationRef);

        if (automationSnap.exists()) {
          const data = automationSnap.data();
          if (data.lastRunDate === today) {
            // Ya se ejecutó hoy a nivel global, cargar los resultados y omitir
            setStatus({
              isRunning: false,
              lastRun: data.lastRunTimestamp?.toDate() || new Date(),
              lastResult: data.lastResult || { success: true, paymentsGenerated: 0, errors: [] }
            });
            return;
          }
        }

        console.log('🤖 [Automation] Iniciando generación global de pagos en Firestore...');
        setStatus(prev => ({ ...prev, isRunning: true }));

        // Ejecutar generación de pagos
        const result = await generateMonthlyPayments(gymId);

        // Guardar resultado
        const resultToSave = {
          success: result.success,
          paymentsGenerated: result.paymentsGenerated,
          errors: result.errors
        };

        // Guardar en Firestore para que todos los clientes estén enterados
        await setDoc(automationRef, {
          lastRunDate: today,
          lastRunTimestamp: serverTimestamp(),
          lastResult: resultToSave
        }, { merge: true });

        setStatus({
          isRunning: false,
          lastRun: new Date(),
          lastResult: resultToSave
        });

        if (result.success) {
          console.log(`✅ [Automation] Completo a nivel global: ${result.paymentsGenerated} pagos generados`);
        } else {
          console.error('❌ [Automation] Errores en ejecución:', result.errors);
        }

      } catch (error) {
        console.error('❌ [Automation] Error crítico:', error);
        setStatus({
          isRunning: false,
          lastRun: new Date(),
          lastResult: {
            success: false,
            paymentsGenerated: 0,
            errors: [error instanceof Error ? error.message : 'Error desconocido']
          }
        });
      }
    };

    runAutomation();
  }, [gymId, enabled, status.isRunning]);

  /**
   * 🔄 Función para forzar ejecución manual
   * Útil para botón de "Generar pagos ahora"
   */
  const runManually = async () => {
    if (!gymId || status.isRunning) return;

    try {
      console.log('🔄 Generación manual iniciada...');
      setStatus(prev => ({ ...prev, isRunning: true }));

      const result = await generateMonthlyPayments(gymId);

      const resultToSave = {
        success: result.success,
        paymentsGenerated: result.paymentsGenerated,
        errors: result.errors
      };

      if (!isMySqlEnabled()) {
        const today = new Date().toISOString().split('T')[0];
        const automationRef = doc(db, `gyms/${gymId}/settings/automation`);
        
        await setDoc(automationRef, {
          lastRunDate: today,
          lastRunTimestamp: serverTimestamp(),
          lastResult: resultToSave
        }, { merge: true });
      }

      setStatus({
        isRunning: false,
        lastRun: new Date(),
        lastResult: resultToSave
      });

      return result;


    } catch (error) {
      console.error('❌ Error en generación manual:', error);
      setStatus({
        isRunning: false,
        lastRun: new Date(),
        lastResult: {
          success: false,
          paymentsGenerated: 0,
          errors: [error instanceof Error ? error.message : 'Error desconocido']
        }
      });
      throw error;
    }
  };

  return {
    ...status,
    runManually
  };
};

export default useMonthlyPaymentsAutomation;